import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {Box, Button, Stack, Typography} from '@mui/material';
import {asset_atom, currency_atom, exchange_name_atom} from 'atoms';
import {clean_number, get_filtered_number, get_price_str, get_value, is_almost_equal} from 'common';
import {request_create_order} from 'connector/order';
import {usePrice} from 'hooks/marketHooks';
import {useActiveOrders} from 'hooks/orderHooks';
import {useCallback, useMemo, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {IOrder, IValueSet} from 'types';

const supported_short_exchanges = ['mock', 'binance'];

export default function OrderButtonBox(props: {
	disabled?: boolean;
	bid_fee: number;
	ask_fee: number;
	total_cost: number;
	order_price: IValueSet;
	target_profit: IValueSet;
	loss_cut: IValueSet;
	handleAI?: () => void;
}) {
	const {disabled, bid_fee, ask_fee, total_cost, order_price, target_profit, loss_cut, handleAI} = props;

	const exchange = useRecoilValue(exchange_name_atom);
	const currency = useRecoilValue(currency_atom);
	const ticker = useRecoilValue(asset_atom);
	const {add_order} = useActiveOrders();
	const [is_processing, set_is_processing] = useState(false);
	const is_short_enabled = useMemo(() => supported_short_exchanges.includes(exchange), [exchange, supported_short_exchanges]);

	const {price: current_price} = usePrice(exchange, ticker);

	const handleOrder = useCallback(
		async (position: 'long' | 'short') => {
			if (!current_price) return;
			set_is_processing(true);

			const order_price_val = clean_number(get_value(order_price));
			const target_price = get_filtered_number(order_price_val * (1 + (position === 'long' ? target_profit.modifier : -target_profit.modifier)));
			const loss_cut_price = get_filtered_number(order_price_val * (1 + (position === 'long' ? loss_cut.modifier : -loss_cut.modifier)));

			// 오르면 산다. is up.
			const direction = is_almost_equal(current_price, order_price_val) ? (position === 'long' ? 'up' : 'down') : order_price_val > current_price ? 'up' : 'down';

			const order: IOrder = {
				order_id: '',
				timestamp: Math.floor(Date.now() / 1000),
				exchange: exchange,
				ticker: ticker,
				state: 'pending',
				heat: 0,
				volume: total_cost / order_price_val,
				order_price: order_price_val,
				target_price: target_price,
				loss_cut_price: loss_cut_price,
				current_price: current_price,
				bid_fee: bid_fee,
				ask_fee: ask_fee,
				tvl: 0,
				profit: 0,
				archived: false,
				direction: direction,
				position: position,
			};

			try {
				const resp = await request_create_order(order);
				if (resp.code === 200) add_order({...order, order_id: resp.data.order_id, state: 'pending'});
			} catch (e) {
				console.error(e);
			} finally {
				setTimeout(() => set_is_processing(false), 1500);
			}
		},
		[order_price, target_profit, loss_cut, total_cost, bid_fee, ask_fee, exchange, ticker, add_order, current_price],
	);

	const short_target = useMemo(
		() =>
			get_value({
				modifier: 1 - target_profit.modifier,
				ref_value: order_price.ref_value,
			}),
		[target_profit, order_price],
	);

	const long_target = useMemo(
		() =>
			get_value({
				modifier: 1 + target_profit.modifier,
				ref_value: order_price.ref_value,
			}),
		[target_profit, order_price],
	);

	const width = '75px';

	return (
		<Box id="order-button" gap="10px" display="flex">
			<Stack alignItems="center" minWidth="110px">
				<Button
					size="small"
					variant="contained"
					color="error"
					onClick={() => handleOrder('short')}
					disabled={disabled || is_processing || !is_short_enabled}
					sx={{width: width}}
				>
					SHORT
				</Button>
				<Typography variant="caption" color="text.disabled">{`${get_price_str(short_target, true)} ${currency}`}</Typography>
			</Stack>

			<Stack alignItems="center" minWidth="110px">
				<Button size="small" variant="contained" color="warning" onClick={handleAI} disabled={disabled || is_processing} sx={{width: width}}>
					AI <AutoAwesomeIcon />
				</Button>
				<Typography variant="caption" color="text.disabled">{`AUTO`}</Typography>
			</Stack>

			<Stack alignItems="center" minWidth="110px">
				<Button size="small" variant="contained" color="primary" onClick={() => handleOrder('long')} disabled={disabled || is_processing} sx={{width: width}}>
					LONG
				</Button>
				<Typography variant="caption" color="text.disabled">{`${get_price_str(long_target, true)} ${currency}`}</Typography>
			</Stack>
		</Box>
	);
}
